'use client'

import { useEffect, useMemo, useState } from 'react'
import { useRouter } from 'next/navigation'
import { useUser } from '@/contexts/UserContext'

type RequireAdminProps = {
  children: React.ReactNode
  /** If false, only requires a token (logged-in user). */
  requireAdmin?: boolean
  /** Optional fallback while checking auth. */
  fallback?: React.ReactNode
}

export function RequireAdmin({ children, requireAdmin = true, fallback = null }: RequireAdminProps) {
  const router = useRouter()
  const { user, loading } = useUser()
  const [hasToken, setHasToken] = useState<boolean | null>(null)

  useEffect(() => {
    try {
      const token = localStorage.getItem('auth_token')
      setHasToken(Boolean(token))
    } catch {
      setHasToken(false)
    }
  }, [])

  const isAuthorized = useMemo(() => {
    if (hasToken !== true) return false
    if (loading) return false
    if (!user) return false
    if (requireAdmin && user.admin !== 1) return false
    return true
  }, [hasToken, loading, user, requireAdmin])

  useEffect(() => {
    if (hasToken === false) {
      router.replace('/login')
      return
    }

    if (hasToken === true && !loading) {
      if (!user) {
        router.replace('/login')
        return
      }

      if (requireAdmin && user.admin !== 1) {
        router.replace('/login')
        return
      }
    }
  }, [hasToken, loading, user, requireAdmin, router])

  if (!isAuthorized) return fallback

  return children
}
